import { Box, Flex, Text } from '@elkaso-app/web-design';
import type { QuotationSupplier } from 'apis/restaurant-inquiries';
import type { IconType } from 'react-icons';
import { FiCreditCard, FiPackage, FiTag, FiTruck } from 'react-icons/fi';

// Compact stat strip for the quotation supplier's commercial terms. One block
// per term — icon, tiny uppercase label, prominent value — separated by
// hairline dividers, on a soft gray card so it reads as one unit without
// stealing space from the item list below. Missing values render '—' (muted):
// an incomplete supplier profile is a data gap worth seeing, same philosophy
// as the request panel.
export const SupplierTermsStrip = ({ profile }: { profile: QuotationSupplier }) => {
  const terms: Array<{ icon: IconType; label: string; value?: string | null }> = [
    { icon: FiTag, label: 'Category', value: profile.category },
    { icon: FiPackage, label: 'MOQ', value: profile.moq },
    { icon: FiCreditCard, label: 'Payment terms', value: profile.paymentTerms },
    { icon: FiTruck, label: 'Delivery', value: profile.delivery },
  ];
  return (
    <Flex
      mt='3'
      bg='gray.50'
      border='1px solid'
      borderColor='gray.200'
      rounded='lg'
      px='4'
      py='2.5'
      align='stretch'
      fontWeight='normal'
      flexWrap={{ base: 'wrap', md: 'nowrap' }}
      rowGap={{ base: '2', md: '0' }}>
      {terms.map(({ icon, label, value }, i) => (
        <Flex
          key={label}
          flex='1'
          flexBasis={{ base: '42%', md: 'auto' }}
          minW='0'
          align='center'
          gap='2.5'
          pl={i ? { base: i % 2 ? '4' : '0', md: '4' } : undefined}
          ml={i ? { base: i % 2 ? '4' : '0', md: '4' } : undefined}
          borderLeft={i ? { base: i % 2 ? '1px solid' : 'none', md: '1px solid' } : undefined}
          borderLeftColor='gray.200'>
          <Box as={icon} color='red.400' flexShrink={0} aria-hidden />
          <Box minW='0'>
            <Text fontSize='9px' color='gray.500' textTransform='uppercase' letterSpacing='wide' lineHeight='1.4'>
              {label}
            </Text>
            {/* Long values (payment terms prose) truncate; hover shows the rest. */}
            <Text
              fontSize='sm'
              fontWeight='semibold'
              color={value ? 'gray.800' : 'gray.400'}
              noOfLines={1}
              wordBreak='break-all'
              title={value || undefined}>
              {value || '—'}
            </Text>
          </Box>
        </Flex>
      ))}
    </Flex>
  );
};
